import axios, { AxiosInstance } from "axios";

/**
 * DTO for individual items in an invoice v2 request
 */
export interface InvoiceV2Item {
  id?: number;
  description: string;
  quantity: number;
  pricePerUnit: number;
  currency: string;
  amount: number;
}

/**
 * DTO for payment instructions in an invoice v2 request
 */
export interface InvoiceV2PaymentInstruction {
  id?: number;
  swiftCode: string;
  accountName: string;
  accountCode: string;
  bankName: string;
  bankBranch: string;
  bankAddress: string;
  remarks: string;
}

/**
 * DTO for invoice v2 request
 */
export interface InvoiceV2Request {
  id?: number;
  requestId?: string;
  employeeId?: number;
  submissionType: string; // FILE_UPLOAD or CREATE_REQUEST
  submissionDate: string; // YYYY-MM-DD
  totalAmount: number;
  status?: string;
  blobId?: number; // For FILE_UPLOAD type
  pdfBlobId?: number; // For CREATE_REQUEST type - UI-generated PDF
  optionalAttachmentBlobId?: number; // For CREATE_REQUEST type - optional user attachment
  attachmentFilePath?: string;
  notes?: string; // Mandatory for FILE_UPLOAD type
  rejectionComment?: string; // Admin's comment when rejecting
  createdAt?: string;
  updatedAt?: string;
  updatedBy?: string;
  isDeleted?: boolean;
  items?: InvoiceV2Item[];
  paymentInstruction?: InvoiceV2PaymentInstruction;
}

/**
 * API Service for Invoice v2 operations
 */
class InvoiceV2ApiService {
  private api: AxiosInstance;
  private baseUrl: string = "/ofc";

  constructor() {
    this.api = axios.create({
      baseURL: this.baseUrl,
      withCredentials: true,
    });

    // Add request interceptor to handle FormData properly
    this.api.interceptors.request.use((config) => {
      // If the data is FormData, remove Content-Type header to let browser set it
      if (config.data instanceof FormData) {
        delete config.headers["Content-Type"];
      }
      return config;
    });
  }

  /**
   * Submit a new invoice file upload (FILE_UPLOAD type)
   */
  async submitInvoiceFileUpload(
    employeeId: number,
    attachmentFile: File,
    username: string,
    originalFileName: string | undefined,
    notes: string,
  ): Promise<InvoiceV2Request> {
    const formData = new FormData();
    formData.append("employeeId", employeeId.toString());
    formData.append("attachment", attachmentFile);
    formData.append("username", username);
    formData.append("notes", notes);

    // Pass original filename if provided
    if (originalFileName) {
      formData.append("originalFileName", originalFileName);
    }

    const response = await this.api.post<InvoiceV2Request>(
      "/invoice/v2/submit/file-upload",
      formData,
    );
    return response.data;
  }

  /**
   * Submit a new invoice create request (CREATE_REQUEST type)
   * Supports sending the UI-generated PDF and optional user attachment separately
   */
  async submitInvoiceCreateRequest(
    employeeId: number,
    request: InvoiceV2Request,
    pdfFile: File | null | undefined,
    optionalAttachmentFile: File | null | undefined,
    username: string,
    pdfFileName?: string,
  ): Promise<InvoiceV2Request> {
    const formData = new FormData();
    formData.append("employeeId", employeeId.toString());

    // Create a Blob with application/json content type for the request part
    const requestBlob = new Blob([JSON.stringify(request)], {
      type: "application/json",
    });
    formData.append("request", requestBlob);

    // PDF file is mandatory for CREATE_REQUEST
    if (pdfFile) {
      formData.append("pdfFile", pdfFile);
    }

    // Optional attachment is optional
    if (optionalAttachmentFile) {
      formData.append("optionalAttachmentFile", optionalAttachmentFile);
    }

    formData.append("username", username);

    // Add PDF filename if provided
    if (pdfFileName) {
      formData.append("pdfFileName", pdfFileName);
    }

    const response = await this.api.post<InvoiceV2Request>(
      "/invoice/v2/submit/create-request",
      formData,
    );
    return response.data;
  }

  /**
   * Get all invoice requests for an employee
   */
  async getInvoiceRequestsByEmployee(
    employeeId: number,
  ): Promise<InvoiceV2Request[]> {
    const response = await this.api.get<InvoiceV2Request[]>(
      `/invoice/v2/list/${employeeId}`,
    );
    return response.data;
  }

  /**
   * Get a specific invoice request
   */
  async getInvoiceRequest(requestId: string): Promise<InvoiceV2Request> {
    const response = await this.api.get<InvoiceV2Request>(
      `/invoice/v2/request/${requestId}`,
    );
    return response.data;
  }

  /**
   * Edit an existing invoice request
   * For FILE_UPLOAD: attachmentFile is optional (can keep previous file)
   * For CREATE_REQUEST: pdfFile is always generated, optionalAttachmentFile is optional
   */
  async editInvoiceRequest(
    requestId: string,
    request: InvoiceV2Request,
    attachmentFile: File | null | undefined,
    pdfFile: File | null | undefined,
    optionalAttachmentFile: File | null | undefined,
    username: string,
    mergedFileName?: string,
    pdfFileName?: string,
  ): Promise<InvoiceV2Request> {
    const formData = new FormData();

    // Create a Blob with application/json content type for the request part
    const requestBlob = new Blob([JSON.stringify(request)], {
      type: "application/json",
    });
    formData.append("request", requestBlob);

    // For FILE_UPLOAD type: attachment is optional
    if (attachmentFile) {
      formData.append("attachment", attachmentFile);
    }

    // For CREATE_REQUEST type: pdfFile and optionalAttachment
    if (pdfFile) {
      formData.append("pdfFile", pdfFile);
    }
    if (optionalAttachmentFile) {
      formData.append("optionalAttachment", optionalAttachmentFile);
    }

    formData.append("username", username);

    // Add filenames if provided
    if (mergedFileName) {
      formData.append("mergedFileName", mergedFileName);
    }
    if (pdfFileName) {
      formData.append("pdfFileName", pdfFileName);
    }

    const response = await this.api.put<InvoiceV2Request>(
      `/invoice/v2/edit/${requestId}`,
      formData,
    );
    return response.data;
  }

  /**
   * Delete an invoice request
   */
  async deleteInvoiceRequest(
    requestId: string,
    username: string,
  ): Promise<void> {
    await this.api.delete(`/invoice/v2/delete/${requestId}`, {
      params: { username },
    });
  }

  /**
   * Update invoice request status (admin only)
   */
  async updateInvoiceRequestStatus(
    requestId: string,
    newStatus: string,
    username: string,
    rejectionComment?: string,
  ): Promise<void> {
    await this.api.post(`/invoice/v2/status/${requestId}`, null, {
      params: { status: newStatus, username, rejectionComment },
    });
  }

  /**
   * Get all invoice requests with a specific status
   */
  async getInvoiceRequestsByStatus(
    status: string,
  ): Promise<InvoiceV2Request[]> {
    const response = await this.api.get<InvoiceV2Request[]>(
      `/invoice/v2/status/${status}`,
    );
    return response.data;
  }

  /**
   * Get all invoice requests with advanced filtering (admin only)
   */
  async getInvoiceRequestsWithFilters(
    startDate?: string,
    endDate?: string,
    status?: string,
    employeeId?: number,
    searchText?: string,
  ): Promise<InvoiceV2Request[]> {
    const params: Record<string, string | number | undefined> = {};
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    if (status) params.status = status;
    if (employeeId) params.employeeId = employeeId;
    if (searchText) params.searchText = searchText;

    const response = await this.api.get<InvoiceV2Request[]>(
      "/invoice/v2/list/admin",
      { params },
    );
    return response.data;
  }

  /**
   * Download attachment for an invoice request
   * For FILE_UPLOAD: downloads the uploaded file
   * For CREATE_REQUEST: downloads the PDF
   */
  async downloadAttachment(requestId: string): Promise<Blob> {
    const response = await this.api.get(`/invoice/v2/download/${requestId}`, {
      responseType: "blob",
    });
    return response.data;
  }

  /**
   * Get optional attachment metadata (filename)
   */
  async getOptionalAttachmentMetadata(
    requestId: string,
  ): Promise<{ fileName: string }> {
    const response = await this.api.get<{ fileName: string }>(
      `/invoice/v2/attachment/optional/${requestId}`,
    );
    return response.data;
  }

  /**
   * Download optional attachment for a CREATE_REQUEST invoice
   */
  async downloadOptionalAttachment(requestId: string): Promise<Blob> {
    const response = await this.api.get(
      `/invoice/v2/download/optional/${requestId}`,
      {
        responseType: "blob",
      },
    );
    return response.data;
  }

  /**
   * Get attachment metadata (filename) for an invoice request
   * Used to determine file type and whether it's viewable
   */
  async getAttachmentMetadata(
    requestId: string,
  ): Promise<{ fileName: string }> {
    const response = await this.api.get<{ fileName: string }>(
      `/invoice/v2/attachment/metadata/${requestId}`,
    );
    return response.data;
  }

  /**
   * Get count of pending invoice requests (SUBMITTED or PROCESSING status)
   * Used for badge notification in admin UI
   */
  async getPendingRequestsCount(): Promise<number> {
    const response = await this.api.get<{ count: number }>(
      `/invoice/v2/count/pending`,
    );
    return response.data.count;
  }

  /**
   * Get count of invoice requests needing attention for an employee (REJECTED or PROCESSED status)
   * Used for badge notification in employee UI
   */
  async getAttentionRequestsCount(employeeId: number): Promise<number> {
    const response = await this.api.get<{ count: number }>(
      `/invoice/v2/count/attention/${employeeId}`,
    );
    return response.data.count;
  }
}

// Export singleton instance
const invoiceV2ApiService = new InvoiceV2ApiService();
export default invoiceV2ApiService;
